<template>
  <v-app>
    <v-app-bar app flat absolute color="transparent" class="glass-bar">
      <div class="boxed-container w-full">
        <div class="d-flex align-center mx-6">
          <router-link to="/" class="d-flex align-center text-decoration-none">
            <v-img
              :src="brandLogoFull"
              height="50px"
              max-width="50px"
              alt="logo"
              contain
              eager
              class="app-logo me-3"
            ></v-img>
          </router-link>

          <v-spacer></v-spacer>

          <language-switcher></language-switcher>
          <!-- <theme-switcher></theme-switcher> -->
          <app-bar-user-menu v-if="isAuthentication"></app-bar-user-menu>
        </div>
      </div>
    </v-app-bar>

    <v-main :style="mainStyle">
      <div class="app-content-container boxed-container pa-6">
        <slot></slot>
      </div>
    </v-main>

    <div class="login-footer-glass">
      <layout-footer></layout-footer>
    </div>
  </v-app>
</template>

<script>
import { computed, onMounted, reactive, ref, watch } from "@vue/composition-api";
import { mdiMagnify, mdiBellOutline, mdiGithub } from "@mdi/js";
import VerticalNavMenu from "./vertical-nav-menu/VerticalNavMenu.vue";
import ThemeSwitcher from "./ThemeSwitcher.vue";
import AppBarUserMenu from "./AppBarUserMenu.vue";
import LanguageSwitcher from "./LanguageSwitcher.vue";
import LayoutFooter from "./LayoutFooter.vue";

export default {
  components: {
    VerticalNavMenu,
    ThemeSwitcher,
    AppBarUserMenu,
    LanguageSwitcher,
    LayoutFooter,
  },
  setup(props, { root }) {
    const brand = computed(() => root.$store.getters.brand);
    const brandLogoFull = computed(() => brand.value?.logoFull || require("@/assets/images/logo-full.png"));
    const isAuthentication = computed(() => root.$store.getters.isAuthentication);
    const searchInput = ref(null);
    const searchItems = reactive(
      root.$router.options.routes
        .filter((each) => each.meta && each.meta.searchLabel)
        .sort((a, b) => {
          const fa = a.meta.searchLabel.toLowerCase();
          const fb = b.meta.searchLabel.toLowerCase();
          if (fa < fb) return -1;
          if (fa > fb) return 1;

          return 0;
        }),
    );

    // Background only for login page
    const isLogin = computed(() => root.$route.name === "login");
    const loginBackgroundUrl = ref(null);
    const getRandomLoginBackground = () => {
      try {
        const context = require.context("@/assets/images/login-page", false, /\.(png|jpe?g|webp)$/);
        const imageModules = context.keys().map(context);
        if (!imageModules || imageModules.length === 0) return null;
        const index = Math.floor(Math.random() * imageModules.length);
        return imageModules[index];
      } catch (e) {
        return null;
      }
    };
    const mainStyle = computed(() => {
      if (!isLogin.value || !loginBackgroundUrl.value) return {};
      return {
        // Add translucent overlay on top of the background image
        backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.35), rgba(255, 255, 255, 0.35)), url(${loginBackgroundUrl.value})`,
        backgroundSize: "cover",
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat",
        minHeight: "100vh",
      };
    });

    watch(
      () => root.$route.name,
      () => {
        loginBackgroundUrl.value = isLogin.value ? getRandomLoginBackground() : null;
      },
      { immediate: true },
    );

    const customFilter = (item, queryText) => {
      const searchText = queryText.toLowerCase();
      const keywords = item.meta.keywords.map((each) => each.toLowerCase());

      return (
        keywords.find((each) => each.indexOf(searchText) > -1) ||
        item.meta.searchLabel.toLowerCase().indexOf(searchText) > -1
      );
    };

    watch(searchInput, (value) => {
      if (!!value && root.$route.name !== value) root.$router.push({ name: value });
    });

    return {
      brandLogoFull,
      isAuthentication,
      searchInput,
      searchItems,
      customFilter,
      mainStyle,
      icons: {
        mdiMagnify,
        mdiBellOutline,
        mdiGithub,
      },
    };
  },
};
</script>

<style lang="scss" scoped>
.v-app-bar ::v-deep {
  .v-toolbar__content {
    padding: 0;

    .app-bar-search {
      .v-input__slot {
        padding-left: 18px;
      }
    }
  }
}

.boxed-container {
  max-width: 1440px;
  margin-left: auto;
  margin-right: auto;
}

.glass-bar {
  background-color: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(6px) saturate(1.2);
  -webkit-backdrop-filter: blur(6px) saturate(1.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.35);
}

.login-footer-glass ::v-deep .v-footer {
  background-color: rgba(255, 255, 255, 0.5) !important;
  backdrop-filter: blur(6px) saturate(1.2);
  -webkit-backdrop-filter: blur(6px) saturate(1.2);
  border-top: 1px solid rgba(255, 255, 255, 0.35);
}
</style>
